import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MultiFactorResolver } from '@angular/fire/auth';
import { AuthService, MfaRequiredError } from '../../services/auth.service';
import { ScrollLockService } from '../../core/scroll-lock.service';
import { EnrolledFactor, MfaService, TotpEnrollment } from '../../services/mfa.service';
import { ErrorMapperService } from '../../services/error-mapper.service';
import { ThemeService } from '../../services/theme.service';
import { ToastService } from '../../services/toast.service';
import { ActionThrottleService } from '../../core/action-throttle.service';
import { ThrottleClickDirective } from '../../directives/throttle-click.directive';
import { modalAnimation } from '../../animations/animations';
import { TwoFactorPanelComponent } from '../two-factor-panel/two-factor-panel.component';
import { MfaChallengeComponent } from '../mfa-challenge/mfa-challenge.component';

type Flow = 'idle' | 'totp' | 'sms-phone' | 'sms-code' | 'reauth' | 'mfa-reauth';

const E164 = /^\+[1-9]\d{7,14}$/;

@Component({
  selector: 'app-settings-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TwoFactorPanelComponent,
    MfaChallengeComponent,
    ThrottleClickDirective,
  ],
  templateUrl: './settings-modal.component.html',
  styleUrls: ['./settings-modal.component.scss'],
  animations: [modalAnimation],
})
export class SettingsModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();

  private authService = inject(AuthService);
  private mfa = inject(MfaService);
  private errorMapper = inject(ErrorMapperService);
  private themeService = inject(ThemeService);
  private toast = inject(ToastService);
  private throttle = inject(ActionThrottleService);
  private recaptcha = viewChild.required<ElementRef<HTMLElement>>('recaptcha');
  private scrollLock = inject(ScrollLockService);

  isDarkMode$ = this.themeService.darkMode$;

  // Account
  email = '';
  emailVerified = false;
  usesPassword = false;
  usesGoogle = false;
  nameControl = new FormControl('', { nonNullable: true, validators: [Validators.maxLength(60)] });
  savingName = false;

  // Two-step verification
  factors: EnrolledFactor[] = [];
  flow: Flow = 'idle';
  enrollment: TotpEnrollment | null = null;
  phoneControl = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(E164)] });
  reauthPassword = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  mfaResolver: MultiFactorResolver | null = null;
  removeCandidate: string | null = null;
  busy = false;
  error = '';

  private smsVerificationId = '';
  private pendingAction: (() => Promise<void>) | null = null;

  ngOnInit(): void {
    this.scrollLock.lock();
    this.refreshState();
  }

  ngOnDestroy(): void {
    this.scrollLock.unlock();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeModal();
  }

  closeModal() {
    this.close.emit();
  }

  get canEnroll(): boolean {
    return this.emailVerified;
  }

  get hasFactors(): boolean {
    return this.factors.length > 0;
  }

  get hasTotp(): boolean {
    return this.factors.some(f => f.kind === 'totp');
  }

  get hasSms(): boolean {
    return this.factors.some(f => f.kind === 'sms');
  }

  private refreshState(): void {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    this.email = user.email ?? '';
    this.emailVerified = user.emailVerified;
    const providers = this.authService.getProviderIds();
    this.usesPassword = providers.includes('password');
    this.usesGoogle = providers.includes('google.com');
    this.factors = this.mfa.listFactors(user);
    if (!this.nameControl.dirty) {
      this.nameControl.setValue(user.displayName ?? '');
    }
  }

  // ── Profile ───────────────────────────────────────────────────────────────
  async saveName(): Promise<void> {
    const name = this.nameControl.value.trim();
    if (!name || this.nameControl.invalid) return;
    const result = await this.throttle.run('settings-name', async () => {
      this.savingName = true;
      try {
        await this.authService.updateDisplayName(name);
        this.nameControl.markAsPristine();
        this.toast.success('Display name updated.', 'Saved');
      } catch (e) {
        this.toast.error(this.errorMapper.map(e).userMessage, 'Could not save');
      } finally {
        this.savingName = false;
      }
    }, { cooldownMs: 3000 });
    if (result.blocked && result.reason === 'cooldown') {
      this.toast.warning(`Please wait ${this.throttle.waitSeconds(result)}s.`, 'Slow down');
    }
  }

  async resendVerification(): Promise<void> {
    const result = await this.throttle.run('settings-verify-email', async () => {
      try {
        await this.authService.resendVerificationEmail();
        this.toast.info('Verification email sent. Check your inbox.', 'Email sent');
      } catch (e) {
        this.toast.error(this.errorMapper.map(e).userMessage, 'Could not send');
      }
    }, { cooldownMs: 60_000 });
    if (result.blocked && result.reason === 'cooldown') {
      this.toast.warning(`Please wait ${this.throttle.waitSeconds(result)}s before requesting another email.`, 'Slow down');
    }
  }

  async checkVerified(): Promise<void> {
    await this.authService.refreshUser();
    this.refreshState();
    if (!this.emailVerified) {
      this.toast.info("We don't see the verification yet. Click the link in your email, then try again.", 'Not verified yet');
    }
  }

  // ── Two-step verification: flows ──────────────────────────────────────────
  cancelFlow(): void {
    this.flow = 'idle';
    this.enrollment = null;
    this.mfaResolver = null;
    this.pendingAction = null;
    this.smsVerificationId = '';
    this.error = '';
    this.busy = false;
    this.phoneControl.reset('');
    this.reauthPassword.reset('');
  }

  // Authenticator app
  async startTotp(): Promise<void> {
    this.error = '';
    await this.withReauth(async () => {
      const user = this.authService.getCurrentUser();
      if (!user) return;
      this.enrollment = await this.mfa.beginTotpEnrollment(user);
      this.flow = 'totp';
    });
  }

  async onTotpCode(code: string): Promise<void> {
    const result = await this.throttle.run('settings-totp-enroll', () => this.withReauth(() => this.doFinishTotp(code)), {
      cooldownMs: 1500,
      backoff: true,
    });
    this.notifyIfCooling(result);
  }

  private async doFinishTotp(code: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user || !this.enrollment) return;
    this.busy = true;
    try {
      await this.mfa.finishTotpEnrollment(user, this.enrollment.secret, code);
      this.afterFactorChange('Authenticator app added.');
    } finally {
      this.busy = false;
    }
  }

  // Text message
  startSms(): void {
    this.error = '';
    this.flow = 'sms-phone';
  }

  async sendSmsCode(): Promise<void> {
    if (this.phoneControl.invalid) {
      this.phoneControl.markAsTouched();
      return;
    }
    // 30 s between texts – SMS costs money and is an abuse target.
    const result = await this.throttle.run('settings-sms-send', () => this.withReauth(() => this.doSendSms()), {
      cooldownMs: 30_000,
    });
    this.notifyIfCooling(result);
  }

  private async doSendSms(): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    this.busy = true;
    this.error = '';
    const verifier = this.mfa.createRecaptcha(this.recaptcha().nativeElement);
    try {
      this.smsVerificationId = await this.mfa.sendEnrollmentSms(user, this.phoneControl.value.trim(), verifier);
      this.flow = 'sms-code';
    } finally {
      verifier.clear();
      this.busy = false;
    }
  }

  async onSmsCode(code: string): Promise<void> {
    const result = await this.throttle.run('settings-sms-enroll', () => this.withReauth(() => this.doFinishSms(code)), {
      cooldownMs: 1500,
      backoff: true,
    });
    this.notifyIfCooling(result);
  }

  private async doFinishSms(code: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    this.busy = true;
    try {
      await this.mfa.finishSmsEnrollment(user, this.smsVerificationId, code);
      this.afterFactorChange('Text-message verification added.');
    } finally {
      this.busy = false;
    }
  }

  // Removal
  async confirmRemove(factor: EnrolledFactor): Promise<void> {
    // An admin account with 2FA is required by firestore.rules for every
    // admin read/write, so removing the only factor would lock them out of
    // their own dashboard. Simplest safe rule: always keep at least one.
    if (this.factors.length <= 1) {
      this.removeCandidate = null;
      this.error = 'You need at least one two-step verification method. Add another one before removing this.';
      return;
    }
    await this.withReauth(async () => {
      const user = this.authService.getCurrentUser();
      if (!user) return;
      await this.mfa.unenroll(user, factor.uid);
      this.removeCandidate = null;
      this.afterFactorChange('Verification method removed.');
    });
  }

  private afterFactorChange(message: string): void {
    this.cancelFlow();
    this.refreshState();
    this.toast.success(message, 'Two-step verification');
  }

  // ── Re-authentication (Firebase requires a recent sign-in for these changes) ──
  private async withReauth(action: () => Promise<void>): Promise<void> {
    try {
      await action();
    } catch (e: any) {
      if (e?.code === 'auth/requires-recent-login') {
        this.pendingAction = action;
        this.flow = 'reauth';
        this.error = '';
        return;
      }
      console.error('Settings action failed:', e?.code ?? e);
      this.error = this.errorMapper.map(e).userMessage;
    }
  }

  async submitReauth(google = false): Promise<void> {
    if (!google && this.reauthPassword.invalid) {
      this.reauthPassword.markAsTouched();
      return;
    }
    const result = await this.throttle.run('settings-reauth', async () => {
      this.busy = true;
      this.error = '';
      try {
        await this.authService.reauthenticate(google ? undefined : this.reauthPassword.value);
        await this.resumePending();
      } catch (e: any) {
        if (e instanceof MfaRequiredError) {
          this.mfaResolver = e.resolver;
          this.flow = 'mfa-reauth';
          return;
        }
        this.error = this.errorMapper.map(e).userMessage;
      } finally {
        this.busy = false;
        this.reauthPassword.reset('');
      }
    }, { cooldownMs: 2000, backoff: true });
    this.notifyIfCooling(result);
  }

  async onReauthMfaVerified(): Promise<void> {
    this.mfaResolver = null;
    await this.resumePending();
  }

  private async resumePending(): Promise<void> {
    const action = this.pendingAction;
    this.pendingAction = null;
    this.flow = 'idle';
    if (action) await this.withReauth(action);
  }

  // ── Password ──────────────────────────────────────────────────────────────
  async sendPasswordReset(): Promise<void> {
    const result = await this.throttle.run('settings-password-reset', async () => {
      try {
        await this.authService.sendPasswordReset(this.email);
        this.toast.info('If your account can use a password, a reset link is on its way.', 'Check your inbox');
      } catch (e) {
        this.toast.error(this.errorMapper.map(e).userMessage, 'Could not send');
      }
    }, { cooldownMs: 30_000 });
    this.notifyIfCooling(result);
  }

  // ── Preferences / session ─────────────────────────────────────────────────
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  async signOut(): Promise<void> {
    await this.authService.signOut();
    this.closeModal();
  }

  private notifyIfCooling(result: { blocked: boolean; reason?: string; retryInMs?: number }): void {
    if (result.blocked && result.reason === 'cooldown') {
      this.error = `Please wait ${this.throttle.waitSeconds({ retryInMs: result.retryInMs ?? 0 })}s before trying again.`;
    }
  }
}
